import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, SectionHeader } from "@/components/marketing/Layout";
import { ArrowRight, Check, X } from "lucide-react";

const TITLE = "Botpress Alternatives for B2B: The Revenue-First Guide (2026)";
const SEO_TITLE = "Botpress Alternatives for B2B in 2026 | Reality Twin";
const DESC =
  "The best Botpress alternatives for B2B teams who want a revenue-focused AI Twin, not a workflow builder: Twin-to-Twin discovery and 60-second setup.";
const URL = "https://realitytwin.io/blog/botpress-alternatives-b2b";
const PUBLISHED = "2026-07-25";

export const Route = createFileRoute("/blog/botpress-alternatives-b2b")({
  head: () => {
    const seo = buildSeo({
      path: "/blog/botpress-alternatives-b2b",
      title: SEO_TITLE,
      description: DESC,
      type: "article",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: TITLE,
            description: DESC,
            datePublished: PUBLISHED,
            dateModified: PUBLISHED,
            author: { "@type": "Person", name: "Ajdin Ruznic" },
            publisher: {
              "@type": "Organization",
              name: "Reality Twin",
              url: "https://realitytwin.io",
            },
            mainEntityOfPage: URL,
            about: [
              { "@type": "Thing", name: "Botpress alternatives" },
              { "@type": "Thing", name: "B2B AI agents" },
              { "@type": "Thing", name: "AI Digital Twin" },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is the best Botpress alternative for B2B?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "For B2B teams focused on revenue rather than support deflection, Reality Twin is the strongest Botpress alternative. Instead of a visual workflow builder, it auto-ingests your website, docs and CRM into a persistent AI Twin that qualifies leads, books meetings and connects with other Twins in the Twin-to-Twin network.",
                },
              },
              {
                "@type": "Question",
                name: "Why do B2B teams switch from Botpress?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Botpress is powerful but developer-centric — it takes weeks to design nodes, intents and flows. B2B teams that need to launch quickly, hand-off to sales, and integrate with CRMs prefer platforms with automatic knowledge ingestion and reasoning agents that don't require flow authoring.",
                },
              },
              {
                "@type": "Question",
                name: "Is Reality Twin hosted in Europe?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Reality Twin is built in Switzerland and hosted in the EU, which makes it a fit for B2B teams with GDPR, DSG or data-residency requirements — a common reason to move away from US-only chatbot platforms.",
                },
              },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Row({ label, twin, bp }: { label: string; twin: string; bp: string }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-b border-border">
      <div className="font-medium">{label}</div>
      <div className="text-sm text-foreground flex gap-2">
        <Check className="h-4 w-4 mt-0.5 shrink-0" /> {twin}
      </div>
      <div className="text-sm text-muted-foreground flex gap-2">
        <X className="h-4 w-4 mt-0.5 shrink-0" /> {bp}
      </div>
    </div>
  );
}

function Page() {
  return (
    <MarketingLayout>
      <article className="mx-auto max-w-3xl px-6 py-20">
        <div className="font-mono-eyebrow mb-3">Guide · Botpress alternatives</div>
        <h1 className="font-serif text-[44px] leading-[1.1] tracking-[-0.02em]">{TITLE}</h1>
        <p className="mt-4 text-[16px] leading-[1.7] text-muted-foreground">
          Botpress is a mature open-source platform loved by developers building complex
          conversational flows. But most B2B teams don't need another workflow builder — they need a
          Twin that reads their website, qualifies buyers and books meetings. Here is a straight
          comparison of the top Botpress alternatives, with a focus on revenue.
        </p>

        <h2 className="font-serif text-[28px] mt-12">The B2B problem with Botpress</h2>
        <p className="mt-3 text-[15px] leading-[1.7] text-muted-foreground">
          Botpress gives you nodes, intents, NLU and a visual designer. That's great when you have
          engineering capacity and a clearly defined flow. It's painful when the goal is simply:
          "read our site, talk to buyers, hand qualified leads to sales." B2B teams end up spending
          weeks authoring flows that a reasoning AI Twin would handle out of the box.
        </p>

        <h2 className="font-serif text-[28px] mt-12">Reality Twin vs. Botpress at a glance</h2>
        <div className="mt-6 hairline rounded-xl p-6 bg-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-3 border-b border-border font-mono-eyebrow text-xs">
            <div>Capability</div>
            <div>Reality Twin</div>
            <div>Botpress</div>
          </div>
          <Row
            label="Setup time"
            twin="~60 seconds — paste URL, Twin ingests site"
            bp="Days to weeks — design flows, intents and NLU"
          />
          <Row
            label="Knowledge"
            twin="Auto-ingested from website, docs, CRM"
            bp="Manually authored in the flow builder"
          />
          <Row
            label="Reasoning"
            twin="LLM-native agent, no scripted paths required"
            bp="Deterministic flows + optional LLM nodes"
          />
          <Row
            label="Twin-to-Twin network"
            twin="Discoverable in the Reality Twin marketplace"
            bp="Not available"
          />
          <Row
            label="Hosting"
            twin="Swiss-built, EU-hosted"
            bp="Self-host or Botpress Cloud (US)"
          />
          <Row
            label="Best for"
            twin="B2B revenue, lead qualification, marketplace discovery"
            bp="Custom developer-built assistants and internal tools"
          />
        </div>

        <h2 className="font-serif text-[28px] mt-12">Other Botpress alternatives worth knowing</h2>
        <ul className="mt-4 space-y-3 text-[15px] leading-[1.7] text-muted-foreground">
          <li>
            <strong className="text-foreground">Chatbase</strong> — quick to launch, but focused on
            support deflection with a single chat widget. See our{" "}
            <Link to="/vs-chatbase" className="underline">
              Reality Twin vs. Chatbase
            </Link>{" "}
            comparison.
          </li>
          <li>
            <strong className="text-foreground">Voiceflow</strong> — strong for voice and IVR
            design; still a flow-first authoring tool.
          </li>
          <li>
            <strong className="text-foreground">Rasa</strong> — open-source and highly customizable,
            requires an ML team.
          </li>
          <li>
            <strong className="text-foreground">Reality Twin</strong> — the revenue-first option:
            auto-ingested knowledge, reasoning agent, Twin-to-Twin discovery.
          </li>
        </ul>

        <h2 className="font-serif text-[28px] mt-12">When to choose Botpress</h2>
        <p className="mt-3 text-[15px] leading-[1.7] text-muted-foreground">
          Pick Botpress when you have a developer team, a deterministic flow requirement, on-prem
          hosting rules, and time to design intents. It is a serious platform for custom
          conversational software.
        </p>

        <h2 className="font-serif text-[28px] mt-12">When to choose Reality Twin</h2>
        <p className="mt-3 text-[15px] leading-[1.7] text-muted-foreground">
          Pick Reality Twin when the goal is B2B revenue: qualifying inbound leads, handing off to
          sales, letting buyers' Twins discover your Twin in the marketplace, and getting live in a
          minute — not a sprint.
        </p>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link
            to="/vs-botpress"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
          >
            Full Reality Twin vs. Botpress comparison <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-full hairline px-5 py-2.5 text-sm"
          >
            See pricing
          </Link>
        </div>
      </article>
      <SectionHeader
        eyebrow="Ready to try"
        title={
          <>
            Launch your Twin in <em className="italic text-muted-foreground">under a minute</em>
          </>
        }
      />
    </MarketingLayout>
  );
}
